import React, { useEffect, useRef, useState } from 'react';
import { FaBell, FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationsContext';
import './NotificationBell.css';

function formatNotificationTime(date) {
  if (!date) return '';
  return date.toLocaleString('sl-SI', {
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function NotificationBell() {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const {
    notifications,
    unreadCount,
    loading,
    markRead,
    markAllRead,
    remove,
  } = useNotifications();

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleOpen = async (notification) => {
    if (!notification.readAt) {
      await markRead(notification.id);
    }
    setOpen(false);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleDelete = async (event, notificationId) => {
    event.preventDefault();
    event.stopPropagation();
    await remove(notificationId);
  };

  return (
    <div
      ref={rootRef}
      className={`notification-bell${open ? ' notification-bell--open' : ''}`}
    >
      <button
        type="button"
        className="notification-bell__trigger"
        id="app-navbar-notifications"
        aria-label="Obvestila"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        <FaBell aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="notification-bell__badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="notification-bell__menu" role="menu" aria-label="Obvestila">
          <div className="notification-bell__header">
            <span className="notification-bell__title">Obvestila</span>
            {unreadCount > 0 && (
              <button
                type="button"
                className="notification-bell__mark-all"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  markAllRead();
                }}
              >
                Označi vse kot prebrano
              </button>
            )}
          </div>

          <div className="notification-bell__list">
            {loading && (
              <div className="notification-bell__empty">Nalaganje…</div>
            )}

            {!loading && notifications.length === 0 && (
              <div className="notification-bell__empty">Ni obvestil.</div>
            )}

            {!loading && notifications.map((notification) => {
              const isUnread = !notification.readAt;
              const headline = notification.body?.trim() || notification.title;
              const context = notification.body?.trim() ? notification.title : null;

              return (
                <div
                  key={notification.id}
                  className={`notification-bell__item${isUnread ? ' notification-bell__item--unread' : ''}`}
                >
                  <span
                    className={`notification-bell__dot${isUnread ? ' notification-bell__dot--active' : ''}`}
                    aria-hidden="true"
                  />
                  <button
                    type="button"
                    className="notification-bell__item-open"
                    onClick={() => handleOpen(notification)}
                  >
                    <span className="notification-bell__item-main">
                      <span className="notification-bell__item-headline">{headline}</span>
                      <span className="notification-bell__item-meta">
                        {context && (
                          <>
                            <span>{context}</span>
                            <span className="notification-bell__meta-sep">·</span>
                          </>
                        )}
                        <span>{formatNotificationTime(notification.createdAt)}</span>
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="notification-bell__delete"
                    aria-label="Izbriši obvestilo"
                    title="Izbriši"
                    onClick={(event) => handleDelete(event, notification.id)}
                  >
                    <FaTrash aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
